import _, {chain, orderBy, sortBy} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {VisualSeenByModel} from '../../../../../../../srcCommon/models/api/visualSeenByModel';
import {AsyncData} from '../../../../../../helpers/asyncDataHelpers';
import {isTracker} from '../../../../../../helpers/modelHelpers';
import {makeMapStateToProps} from '../../../../../../helpers/reselectHelpers';
import {ConversationSortPropertiesEnum} from '../../../../../../models/api/conversationCommonTypes';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {SeenByModel} from '../../../../../../models/api/seenByModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {AppState} from '../../../../../../store/appState';
import {FindTeammate, getFindTeammate} from '../../../../../../store/data/teammates/lazyTeammatesSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InviteDropdownTrackersContent, SortedSubscribersInfo} from './inviteDropdownTrackersContent';
import {VisualConversationTrackerModel} from './inviteHelpers';

/*
 * Props.
 */

interface InviteDropdownTrackersContainerProps {
  visualTrackers: ReadonlyArray<VisualConversationTrackerModel>;
  conversation: ConversationModel;
  conversationInboxesTeammateIds: ReadonlyArray<TeammateModel['id']>;
  cellsContext: CellsContext;
  seenBy: AsyncData<ReadonlyArray<SeenByModel>>;
  userId: number;
}

/*
 * Selectors.
 */

function getConversationInboxesTeammateIds(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.conversationInboxesTeammateIds;
}

function getConversation(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.conversation;
}

function getVisualTrackers(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.visualTrackers;
}

function getCellsContext(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.cellsContext;
}

function getUserOrDelegatedTeammateId(_state: unknown, props: {cellsContext: CellsContext}) {
  return props.cellsContext.teammateId;
}

function getSeenBy(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.seenBy;
}

function getUserId(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.userId;
}

function makeGetSortedSubscribersInfo() {
  return createSelector(
    [
      getVisualTrackers,
      getConversation,
      getConversationInboxesTeammateIds,
      getSeenBy,
      getUserId,
      getFindTeammate,
      getUserOrDelegatedTeammateId,
    ],
    (
      visualTrackers,
      conversation,
      conversationInboxesTeammateIds,
      seenBy,
      userId,
      findTeammate,
      userOrDelegatedTeammateId,
    ) =>
      computeSortedSubscribersInfo({
        visualTrackers,
        conversation,
        conversationInboxesTeammateIds,
        seenBy,
        userId,
        findTeammate,
        userOrDelegatedTeammateId,
      }),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InviteDropdownTrackersContainerProps>()(() => ({
  sortedSubscribersInfo: makeGetSortedSubscribersInfo(),
  cellsContext: getCellsContext,
}));

export const InviteDropdownTrackersContentContainer = connect(mapStateToProps)(InviteDropdownTrackersContent);

/*
 * Helpers.
 */

interface ComputeSortedSubscribersInfoParams {
  visualTrackers: ReadonlyArray<VisualConversationTrackerModel>;
  conversation: ConversationModel;
  seenBy: AsyncData<ReadonlyArray<SeenByModel>>;
  userId: number;
  userOrDelegatedTeammateId: number;
  findTeammate: FindTeammate;
  conversationInboxesTeammateIds: ReadonlyArray<TeammateModel['id']>;
}

/**
 * Computes subscribers and non-subscribers participants lists for display.
 *
 * The non-subscribers will not include those who don't currently have access to the conversation.
 */
export function computeSortedSubscribersInfo({
  visualTrackers,
  conversation,
  conversationInboxesTeammateIds,
  seenBy,
  userId,
  userOrDelegatedTeammateId,
  findTeammate,
}: ComputeSortedSubscribersInfoParams): SortedSubscribersInfo {
  // Subscribers to potentially display below the header.
  const subscribers = visualTrackers.filter((visualTracker) => !visualTracker.hasMuted);

  // Muted users to potentially display below the header.
  const mutedUsers = visualTrackers.filter((visualTracker) => visualTracker.hasMuted);

  // Participants who have seen the conversation and still have access to it.
  const visualSeenByList: ReadonlyArray<VisualSeenByModel> = computeParticipantsVisualSeenByList(
    seenBy,
    conversation,
    findTeammate,
    conversationInboxesTeammateIds,
  );

  // Filter out users who have seen the conversation but are already subscribers or have muted.
  const subscribersIds = subscribers.map((visualTracker) => visualTracker.teammate.id);
  const mutedUsersIds = mutedUsers.map((visualTracker) => visualTracker.teammate.id);
  const filteredSeenByTrackers = _(visualSeenByList)
    .filter(({teammate}) => !(subscribersIds.includes(teammate.id) || mutedUsersIds.includes(teammate.id)))
    .map(
      (seenByData): VisualConversationTrackerModel => ({
        teammate: seenByData.teammate,
        hasSeenAll: seenByData.hasSeenAll,
        seenAt: seenByData.lastSeenAt,
        isUser: seenByData.teammate.id === userId,
        isUserOrDelegatedTeammate: seenByData.teammate.id === userOrDelegatedTeammateId,
        hasMuted: true,
        isReader: true,
      }),
    )
    .value();

  // unsubscribed consists of either users who have muted the conversation or just read it in a team inbox.
  const nonSubscribers = [...mutedUsers, ...filteredSeenByTrackers];

  // make sure the user is at the front of the section it belongs in
  const sortedSubscribers = sortBy(
    subscribers,
    (visualTracker) => visualTracker.teammate.id !== userOrDelegatedTeammateId,
  );
  // we prioritize sorting by teammateId, then by seenAt.
  const sortedNonSubscribers = orderBy(
    nonSubscribers,
    [(visualTracker) => visualTracker.teammate.id !== userOrDelegatedTeammateId, (tracker) => tracker.seenAt],
    ['asc', 'desc'],
  );

  return {
    subscribers: sortedSubscribers,
    nonSubscribers: sortedNonSubscribers,
  };
}

/**
 * Computes list representing teammates who have seen the conversation who currently have access to the conversation.
 *
 * The teammate has access to the conversation if they have an active tracker for the conversation or
 * has access to one of the conversation's inboxes.
 *
 * The teammate may have seen the conversation and then had their tracker revoked or their inbox access revoked.
 * In those cases, they won't be represented in this list.
 */
function computeParticipantsVisualSeenByList(
  seenBy: AsyncData<ReadonlyArray<SeenByModel>>,
  conversation: ConversationModel,
  findTeammate: FindTeammate,
  conversationInboxesTeammateIds: ReadonlyArray<TeammateModel['id']>,
): ReadonlyArray<VisualSeenByModel> {
  const seenByList = seenBy.status === AsyncStatusesEnum.LOADED ? seenBy.data : [];

  // Performance optimization.
  if (seenByList.length === 0) {
    return [];
  }

  return chain(seenByList)
    .map((teammateSeenBy): VisualSeenByModel | null => {
      const teammate = findTeammate(
        teammateSeenBy.teammateId,
        'InviteDropdownTrackersContentContainer computeParticipantsVisualSeenByList',
      );
      const lastSeenAt = teammateSeenBy.autoSeenAt;
      if (!teammate || !lastSeenAt) {
        return null;
      }

      const isTeammateMemberOfAConversationInbox = conversationInboxesTeammateIds.includes(teammate.id);

      const tracker = conversation.trackers[teammate.id];
      const hasActiveTracker = isTracker(tracker);

      const canTeammateAccessConversation = isTeammateMemberOfAConversationInbox || hasActiveTracker;

      return canTeammateAccessConversation
        ? {
            teammate,
            lastSeenAt,
            hasSeenAll: lastSeenAt >= conversation.orders[ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE],
          }
        : null;
    })
    .compact()
    .sortBy((seenAt) => -seenAt.lastSeenAt)
    .value();
}
